import { HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { useEffect, useReducer, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../state/store";
import { ajaxController } from "../../ajax/controller/AjaxController";
import { isOnlineNow, subscribeToReachability } from "../../offline/connectivity";
import { parseDateTimes } from "../../utilities/helpers/parseDateTimes";
import { useLatestRef } from "./useLatestRef";

export type SignalRState = {
  connection: HubConnection | null;
  isConnecting: boolean;
  isReconnecting: boolean;
  error: string | null;
  hasCompletedInitialConnect: boolean;
};

type SignalRAction =
  | { type: "CONNECT_START" }
  | { type: "CONNECT_SUCCESS"; payload: HubConnection }
  | { type: "CONNECT_ERROR"; payload: string }
  | { type: "RECONNECT_START" }
  | { type: "RECONNECT_SUCCESS" }
  | { type: "DISCONNECTED"; payload?: string };

type HubHandler = (...args: any[]) => void;

// Note: off is overridden too because SignalR removes a handler by identity, and what on registered is
// the wrapper rather than the caller's function, so a plain off would find nothing to remove.
export function attachDateInterceptor(connection: HubConnection) {
  const originalOn = connection.on.bind(connection);
  const originalOff = connection.off.bind(connection);
  const wrappers = new Map<string, Map<HubHandler, HubHandler>>();

  connection.on = (methodName: string, newCallback: HubHandler) => {
    const name = methodName.toLowerCase();
    const byCallback = wrappers.get(name) ?? new Map<HubHandler, HubHandler>();
    if (byCallback.has(newCallback)) return;

    const wrapper: HubHandler = (...args: any[]) => {
      const revivedArgs = args.map(arg => {
        if (typeof arg === "object" && arg !== null) {
          parseDateTimes(arg);
          return arg;
        }
        return arg;
      });

      newCallback(...revivedArgs);
    };

    byCallback.set(newCallback, wrapper);
    wrappers.set(name, byCallback);
    originalOn(methodName, wrapper);
  };

  connection.off = (methodName: string, method?: HubHandler) => {
    const name = methodName.toLowerCase();

    if (!method) {
      wrappers.delete(name);
      originalOff(methodName);
      return;
    }

    const byCallback = wrappers.get(name);
    const wrapper = byCallback?.get(method);
    if (!byCallback || !wrapper) return;

    byCallback.delete(method);
    if (byCallback.size === 0) wrappers.delete(name);
    originalOff(methodName, wrapper);
  };
}
export function joinViewGroup(connection: HubConnection, view: string) {
  return connection
    .invoke("ChangeViewAsync", view)
    .catch((err) => console.warn("ChangeViewAsync refused", err));
}

export function signalRReducer(state: SignalRState, action: SignalRAction): SignalRState {
  switch (action.type) {
    case "CONNECT_START":
      return { ...state, isConnecting: true, isReconnecting: false };
    case "CONNECT_SUCCESS":
      return { ...state, connection: action.payload, isConnecting: false, isReconnecting: false, error: null, hasCompletedInitialConnect: true };
    case "CONNECT_ERROR":
      return { ...state, isConnecting: false, isReconnecting: false, error: action.payload, hasCompletedInitialConnect: true };
    case "RECONNECT_START":
      return { ...state, isReconnecting: true, error: null };
    case "RECONNECT_SUCCESS":
      return { ...state, isReconnecting: false, error: null };
    case "DISCONNECTED":
      return { ...state, connection: null, isConnecting: false, isReconnecting: false, error: action.payload ?? null };
    default:
      return state;
  }
}

export function useSignalR(hubUrl: string) {
  const [state, dispatch] = useReducer(signalRReducer, {
    connection: null,
    isConnecting: false,
    isReconnecting: false,
    error: null,
    hasCompletedInitialConnect: false,
  });

  const currentUserPushNotificationPreferences = useSelector((state: RootState) => state.authReducer.user?.pushNotificationEnabled);
  const userId = useSelector((state: RootState) => state.authReducer.user?.id);
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const token = useSelector((state: RootState) => state.authReducer.accessToken);

  const userIdRef = useLatestRef(userId);
  const currentViewRef = useLatestRef(currentView);
  const tokenRef = useLatestRef(token);
  const connectionRef = useRef<HubConnection | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const isSignedIn = !!token;

  useEffect(() => {
    if (!isSignedIn) return;

    let cancelled = false;
    const connection = new HubConnectionBuilder()
      // Note: asked for on every connect and reconnect. The token held in the store is an hour old by the time
      // a tablet that spent the night offline reconnects, and the hub refused it until some other request
      // happened to renew it.
      .withUrl(hubUrl, { accessTokenFactory: async () => (await ajaxController.getAccessToken()) ?? tokenRef.current ?? "" })
      .withAutomaticReconnect()
      .build();

    attachDateInterceptor(connection);
    connectionRef.current = connection;

    // Note: the hub decides which machine-position groups a connection is in from the user's zones when
    // the view is joined, so a zone change has to re-join the current view or the map keeps the old set.
    connection.on("UserInfoChangedAsync", () => {
      const view = currentViewRef.current;
      if (view) joinViewGroup(connection, view);
    });

    connection.onreconnecting((error) => {
      console.warn("Connection lost. Reconnecting...", error);
      dispatch({ type: "RECONNECT_START" });
    });

    connection.onreconnected(() => {
      const uid = userIdRef.current;
      if (!uid) {
        dispatch({ type: "RECONNECT_SUCCESS" });
        return;
      }
      connection
        .invoke("RegisterUserAsync")
        .then(() => joinViewGroup(connection, currentViewRef.current ?? "None"))
        .then(() => dispatch({ type: "RECONNECT_SUCCESS" }))
        .catch((err) => {
          console.error("Reconnection registration failed", err);
          dispatch({ type: "CONNECT_ERROR", payload: err.message });
        });
    });

    connection.onclose((error) => {
      if (cancelled) return;
      console.error("Connection closed due to error:", error);
      dispatch({ type: "DISCONNECTED", payload: error?.message ?? "Connection closed" });
    });

    dispatch({ type: "CONNECT_START" });

    const startPromise = connection.start();

    startPromise
      .then(() => {
        if (cancelled) return;
        const uid = userIdRef.current;
        if (!uid) return;
        return connection.invoke("RegisterUserAsync");
      })
      .then(() => {
        if (cancelled) return;
        const view = currentViewRef.current;
        if (!view) return;
        return joinViewGroup(connection, view);
      })
      .then(() => {
        if (cancelled) return;
        dispatch({ type: "CONNECT_SUCCESS", payload: connection });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Connection failed:", err);
        dispatch({ type: "CONNECT_ERROR", payload: err.message });
      });

    return () => {
      cancelled = true;
      connectionRef.current = null;
      startPromise
        .then(() => connection.stop())
        .catch(() => { /* start already failed; nothing to stop */ });
    };
  }, [hubUrl, isSignedIn, userIdRef, currentViewRef, tokenRef, retryNonce]);

  useEffect(() => {
    if (!isSignedIn || !state.error) return;

    const retry = () => {
      if (isOnlineNow()) setRetryNonce((n) => n + 1);
    };

    const unsubscribe = subscribeToReachability(retry);
    const intervalId = window.setInterval(retry, 20000);

    return () => {
      unsubscribe();
      window.clearInterval(intervalId);
    };
  }, [state.error, isSignedIn]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || connection.state !== HubConnectionState.Connected || !currentView) return;
    joinViewGroup(connection, currentView);
  }, [currentView, state.connection]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || connection.state !== HubConnectionState.Connected) return;
    connection
      .invoke("ChangePushPreferenceAsync", currentUserPushNotificationPreferences ?? false)
      .catch((err) => console.error("ChangePushPreferenceAsync failed", err));
  }, [currentUserPushNotificationPreferences, state.connection]);

  return state;
}
